import { jsx } from 'react/jsx-runtime';
import * as React from 'react';
import styles from '../styles/uitk-egds-utils.css';
import styles$1 from '../styles/uitk-egds-foundation.css';

/*
  This utility does two things: 1) Always returns a list of class names ready to be placed into the "className" property 2) Sometimes scopes class names to the component.
  It is a TypeScript version of a function originally copied from https://github.com/JedWatson/classnames/blob/master/index.js
*/
function classNames(...args) {
    const classes = [];
    /*
      If javascript is not in strict mode, `this` equals `window`. Unless it's been set via `bind`, `call` or `apply`.
      If the function has not been called with any of the methods above, i.e. `classNames.bind(definedThisValue)`, `this.?[arg]` could return a value from the window object (if the class name matches a property on the window object).
      To avoid this problem, we set `_this` equal to undefined if `this === window`.
    */
    const _this = typeof window !== 'undefined' && this === window ? undefined : this;
    for (const arg of args) {
        if (!arg) {
            // eslint-disable-next-line no-continue
            continue;
        }
        const argType = typeof arg;
        if (argType === 'string') {
            const argStrToArray = arg.split(' ');
            if (_this && argStrToArray.length > 1) {
                // If multiple classes passed in a string, handle them as an array
                classes.push(classNames.apply(_this, argStrToArray));
            }
            else {
                classes.push(_this?.[arg] || arg); // If _this exists, the class will be scoped to its component
            }
        }
        else if (Array.isArray(arg)) {
            [].concat(...arg).filter(Boolean).length && classes.push(classNames.apply(_this ?? null, arg)); // Filter out empty, undefined, false arrays/array values and then check the length
        }
        else if (argType === 'object') {
            if (arg.toString === Object.prototype.toString) {
                for (const key in arg) {
                    // eslint-disable-next-line max-depth
                    if ({}.hasOwnProperty.call(arg, key) && arg[key]) {
                        classes.push(_this?.[key] || key); // If _this exists, the class will be scoped to its component
                    }
                }
            }
            else {
                classes.push(arg.toString());
            }
        }
    }
    return classes.join(' ');
}

var Level;
(function (Level) {
    Level["ASSERTIVE"] = "assertive";
    Level["POLITE"] = "polite";
})(Level || (Level = {}));
const liveAnnounceId = 'uitk-live-announce';
const findOrCreateContainer = (id, ref) => {
    const container = document.createElement('div');
    container.id = id || liveAnnounceId;
    const refParentNode = ref ? document.body.querySelector(`#${id}`)?.parentNode : undefined;
    const bodyParentNode = document.body.querySelector(`#${liveAnnounceId}`)?.parentNode;
    // Use existing live-announce div in ref container
    if (refParentNode) {
        return refParentNode.querySelector(`#${id}`);
    }
    if (ref?.current) {
        // Create new live-announce div in ref container
        return ref.current.appendChild(container);
    }
    // Use existing live-announce container at bottom of page
    if (bodyParentNode) {
        return document.body.querySelector(`#${liveAnnounceId}`);
    }
    // Create new live-announce at bottom of page
    return document.body.appendChild(container);
};
// Ref and Id added to fix Dialog Typeahead
// not seeing live announce
// https://jira.expedia.biz/browse/WCAG-10081
const liveAnnounce = (msg = '', level = Level.ASSERTIVE, className = 'is-visually-hidden', ref, id) => {
    // If no browser, exit early
    /* istanbul ignore if */
    if (!(typeof document !== 'undefined' && typeof document.querySelector === 'function')) {
        return;
    }
    // Find it or create it
    const liveRegion = findOrCreateContainer(id, ref);
    // Clear out exist contents so they are not read
    liveRegion.textContent = '';
    liveRegion.setAttribute('aria-live', level);
    liveRegion.setAttribute('class', className);
    // Set msg after a delay so screenreaders will announce duplicate msgs
    setTimeout(() => {
        liveRegion.textContent = msg;
    }, 500);
};

const documentHead = () => document.querySelector('head') ||
    (() => {
        const h = document.createElement('head');
        return document.appendChild(h);
    })();
var ResolutionStrategy;
(function (ResolutionStrategy) {
    ResolutionStrategy[ResolutionStrategy["IMMEDIATELY"] = 0] = "IMMEDIATELY";
    ResolutionStrategy[ResolutionStrategy["ON_LOAD_EVENT"] = 1] = "ON_LOAD_EVENT";
})(ResolutionStrategy || (ResolutionStrategy = {}));
const loadScript = (src, loadIntoHead, scriptId = 'temp-script', resolution = ResolutionStrategy.ON_LOAD_EVENT, timeoutDuration = undefined
// eslint-disable-next-line max-params
) => {
    const scriptPromise = new Promise((resolve, reject) => {
        if (typeof document === 'undefined') {
            reject(new Error('document is not defined'));
        }
        else {
            const prev = document.getElementById(scriptId);
            if (prev) {
                prev.remove();
            }
            const script = document.createElement('script');
            script.setAttribute('type', 'text/javascript');
            script.setAttribute('id', scriptId);
            script.setAttribute('src', src);
            script.async = true;
            script.defer = true;
            if (resolution === ResolutionStrategy.ON_LOAD_EVENT) {
                script.onload = () => {
                    resolve();
                };
            }
            const appendTarget = loadIntoHead ? documentHead() : document.body;
            const onScriptLoadError = () => {
                appendTarget.removeChild(script);
                if (resolution === ResolutionStrategy.IMMEDIATELY) {
                    console.error(`Failed to load ${src}`);
                }
                else {
                    reject(new Error(`Failed to load ${src}`));
                }
            };
            script.addEventListener('error', onScriptLoadError);
            appendTarget.appendChild(script);
            if (resolution === ResolutionStrategy.IMMEDIATELY) {
                resolve();
            }
        }
    });
    if (timeoutDuration) {
        const timeoutPromise = new Promise((resolve, reject) => {
            setTimeout(() => {
                reject(new Error('Timeout: Map API initialization took too long'));
            }, timeoutDuration);
        });
        return Promise.race([scriptPromise, timeoutPromise]);
    }
    return scriptPromise;
};
const checkScriptIsLoaded = (scriptName, checkApiIsLoaded, checkApiIsLoading, retry) => new Promise((resolve, reject) => {
    const { delay, maxRetries, retriesLeft } = retry;
    const apiIsLoaded = checkApiIsLoaded();
    const apiIsLoading = checkApiIsLoading();
    if (apiIsLoaded)
        resolve(true);
    if (!apiIsLoading)
        reject(new Error(`${scriptName} is not loaded`));
    const count = retriesLeft ?? maxRetries;
    if (count > 0) {
        setTimeout(() => checkScriptIsLoaded(scriptName, checkApiIsLoaded, checkApiIsLoading, {
            delay,
            maxRetries,
            retriesLeft: count - 1,
        })
            .then(resolve)
            .catch(reject), delay);
    }
    else
        reject(new Error(`${scriptName} was not detected as loaded after ${delay * maxRetries}ms`));
});
class ScriptLoader {
    static getScript(url, loadIntoHead, loadScriptFn = loadScript) {
        return loadScriptFn(url, loadIntoHead).then(() => true);
    }
}

// eslint-disable-next-line
classNames.bind(styles);
const EGDSContext = React.createContext({ context: null });
const useEGDSContext = () => React.useContext(EGDSContext);
// Wrapping component and render prop so class components can access useEGDSContext
const EGDSContextWrapper = ({ children }) => {
    const { context, data } = useEGDSContext();
    return children(context, data);
};
const EGDSContextProvider = ({ children, config, }) => jsx(EGDSContext.Provider, { value: config, children: children });
EGDSContextProvider.displayName = 'EGDSContextProvider';

if (typeof document !== 'undefined') {
    // Detect iOS devices
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
    const addBodyClass = () => {
        if (isIOS)
            return; // Do nothing on iOS
        if (!document.body.classList.contains('uitk-no-outline')) {
            document.body.classList.add('uitk-no-outline');
            // Manage event listeners
            document.body.removeEventListener('mousedown', addBodyClass);
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            document.body.addEventListener('keydown', removeBodyClass);
        }
    };
    const removeBodyClass = () => {
        if (isIOS)
            return; // Do nothing on iOS
        if (document.body.classList.contains('uitk-no-outline')) {
            document.body.classList.remove('uitk-no-outline');
            // Manage event listeners
            document.body.removeEventListener('keydown', removeBodyClass);
            document.body.addEventListener('mousedown', addBodyClass);
        }
    };
    // Auto-initialize (only if not iOS)
    if (!isIOS)
        addBodyClass();
}

const getClassNameList = classNames.bind(styles$1);
const getColorAttributes = (config) => {
    const themeClasses = getClassNameList(config?.color?.lightMode?.isThemeBase && !config?.color?.lightMode?.isGradient && 'uitk-light-mode-is-base', config?.color?.darkMode?.isThemeBase && !config?.color?.darkMode?.isGradient && 'uitk-dark-mode-is-base', config?.color?.lightMode?.isGradient && 'uitk-light-mode-is-gradient', config?.color?.darkMode?.isGradient && 'uitk-dark-mode-is-gradient');
    return {
        'data-theme-light': config?.color?.lightMode?.colorTheme,
        'data-theme-dark': config?.color?.darkMode?.colorTheme,
        className: themeClasses,
    };
};
const getSpacingAttributes = (config) => ({
    'data-theme-spacing': config?.spacing?.spacingTheme,
});
const getThemeAttributes = (config) => {
    let attributes = {};
    if (config?.color) {
        attributes = { ...attributes, ...getColorAttributes(config) };
    }
    if (config?.spacing) {
        attributes = { ...attributes, ...getSpacingAttributes(config) };
    }
    return attributes;
};
const getThemeContainerAttributes = (props) => {
    const { config } = props;
    let attributes = {};
    if (config?.color?.isColorThemeContainer) {
        const { className, ...attrs } = getColorAttributes(config);
        const containerColorClasses = { className: getClassNameList('uitk-theme-provider-container', className) };
        attributes = { ...attributes, ...attrs, ...containerColorClasses };
    }
    if (config?.spacing?.spacingTheme) {
        attributes = { ...attributes, ...getSpacingAttributes(config) };
    }
    return attributes;
};

const ThemeContext = React.createContext(undefined);
const EGDSThemeProvider = (props) => {
    const { children, isColorThemeContainer } = props;
    let { config: value } = props;
    /*
     The following mapping is necessary to ensure the legacy color props i.e. `config.lightMode` and `config.darkMode` are correctly mapped to the new color prop structure i.e. `config.color.lightMode` and `config.color.darkMode`.
    */
    value = {
        ...value,
        color: {
            ...value.color,
            ...(value?.lightMode && { lightMode: value.lightMode }),
            ...(value?.darkMode && { darkMode: value.darkMode }),
            ...(isColorThemeContainer && { isColorThemeContainer }),
        },
    };
    // Remove legacy color props
    delete value.lightMode;
    delete value.darkMode;
    return (jsx(ThemeContext.Provider, { value: value, children: value?.color?.isColorThemeContainer || value?.spacing?.spacingTheme ? (jsx("section", { ...getThemeContainerAttributes({ config: value}), children: children })) : (children) }));
};
const useTheme = () => {
    const context = React.useContext(ThemeContext);
    // In order to implement theming, please wrap your component tree with the EGDSThemeProvider
    return context ? getThemeAttributes(context) : {};
};

export { EGDSContext as E, Level as L, ResolutionStrategy as R, ScriptLoader as S, ThemeContext as T, liveAnnounceId as a, EGDSContextWrapper as b, classNames as c, EGDSContextProvider as d, checkScriptIsLoaded as e, loadScript as f, EGDSThemeProvider as g, useTheme as h, documentHead as i, liveAnnounce as l, useEGDSContext as u };
//# sourceMappingURL=index-chunk.esm.js.map
